import { useId, type ReactElement, type SVGProps } from 'react';
import type { ModuleId } from './modules';

/**
 * The six BRAND icons, one per module.
 *
 * Separate from `components/ModuleIcon.tsx`, which holds the monochrome
 * `currentColor` glyph set used for navigation items inside a module.
 * These are different things and should not be merged: a glyph inherits
 * the text colour and signals state (active, hover, disabled); a brand
 * mark carries its own colours and signals IDENTITY. Making the brand
 * marks follow `currentColor` would throw away the thing that makes them
 * recognisable.
 *
 * WHY .tsx AND NOT .ts: the file contains JSX. A .ts file cannot hold
 * JSX, and the alternatives - raw SVG strings rendered with
 * dangerouslySetInnerHTML, or hand-built createElement trees - are both
 * worse than the one-letter extension.
 *
 * WHY useId(): several of these carry <filter> elements, and an SVG
 * filter is referenced by a GLOBAL document id. The exported artwork uses
 * fixed ids like "filter0_d_798_5717", so rendering the same icon twice on
 * one page - which happens the moment the rail and the login picker are
 * both mounted - would give two elements the same id. The browser then
 * resolves every reference to whichever came first, and the second icon
 * renders with the first one's filter. Prefixing with useId() makes each
 * instance self-contained.
 */

type IconProps = Omit<SVGProps<SVGSVGElement>, 'viewBox' | 'xmlns'>;

/** Shared defaults. Size comes from the caller's className, as everywhere else. */
function svgProps(props: IconProps) {
  return {
    viewBox: '0 0 100 100',
    fill: 'none' as const,
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
    focusable: false,
    ...props,
  };
}

/** To-Do - a tick sweeping out of a list. */
export function TodoIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path
        d="M50.0893 70.9021C52.2011 68.7652 54.8504 65.5485 56.8951 63.2316L71.2639 46.8407L75.7262 41.7601C76.5741 40.7979 77.697 39.4047 78.6863 38.6218C79.9578 37.6156 82.6184 37.6597 83.7561 38.9022C84.7232 39.9585 85.3419 41.335 84.9418 42.8018C84.6988 43.6929 84.169 44.3713 83.5839 45.0663C82.142 46.7464 80.6767 48.4056 79.2197 50.0723L69.3078 61.4047L58.8852 73.3963C57.0294 75.533 55.151 77.7685 53.195 79.8214C52.3289 80.7304 51.5282 80.9837 50.2715 80.9996C48.2219 81.0257 47.3284 79.8388 45.9921 78.4418L37.7935 69.8423C36.9933 69.0082 34.6834 66.4092 33.8504 65.946C32.0404 65.915 29.1076 66.0417 27.4522 65.8912C27.0569 65.5124 26.1663 65.3067 25.5487 64.6599C23.8014 62.8297 23.8605 59.8375 23.8611 57.5053C27.2724 57.443 31.7389 57.4294 35.1148 57.5828C36.5465 57.5173 37.7086 57.9828 38.7192 58.9857C40.252 60.5067 41.7103 62.1541 43.2193 63.7136L47.6894 68.3325C48.4413 69.116 49.4092 70.077 50.0893 70.9021Z"
        fill="#6DAAFD"
      />
      <path
        d="M23.8611 57.5024C27.2724 57.4401 31.7389 57.4265 35.1148 57.5798C31.9888 58.469 30.4363 61.7747 32.5603 64.257C32.8295 64.5716 33.7393 65.6456 33.8504 65.9431C32.0404 65.9121 29.1076 66.0388 27.4522 65.8883C27.0569 65.5095 26.1663 65.3038 25.5487 64.657C23.8014 62.8268 23.8605 59.8345 23.8611 57.5024Z"
        fill="#4876B3"
      />
      <path
        d="M15.5719 28.179C16.0235 26.4119 17.7753 24.9326 18.9524 23.6068C19.8865 22.5547 20.9412 21.4838 21.8726 20.4731C23.2945 18.9299 24.9227 18.0094 27.0565 18.1129C36.441 18.1064 45.8301 18.1135 55.2128 18.1131L64.0688 18.1108C65.6873 18.1108 67.3826 18.0548 68.9926 18.2205C69.5746 18.2804 70.4115 18.8466 70.8078 19.2651C73.0333 21.6157 72.036 26.0521 68.4501 26.2958C66.772 26.3999 65.1731 26.3305 63.5411 26.3291L53.6541 26.3297L34.8803 26.3258C31.2397 26.3279 27.4765 26.3798 23.8501 26.3167C21.7371 26.2186 17.7944 26.0845 16.3127 27.638C16.0869 27.8747 15.8462 28.2473 15.6735 28.2643L15.5719 28.179Z"
        fill="#6DAAFD"
      />
      <path
        d="M23.8439 38.3534C25.5444 37.7448 32.0447 37.9138 34.1098 37.9117L44.0194 37.9037L54.2035 37.899C55.9927 37.8988 61.0173 37.7096 62.3947 38.3153C65.5195 39.6894 65.1143 45.9402 60.7983 46.1204C60.029 46.2705 57.8142 46.2193 56.9299 46.2176L50.0812 46.2048L23.8532 46.2028C23.8577 47.2765 23.9134 48.8845 23.8488 49.9154C23.7593 48.7426 23.6377 47.4873 23.6366 46.3176C23.1694 46.2452 22.6215 46.006 21.7853 45.9867C20.949 45.9675 20.4734 46.0127 19.1859 46.3176L18.0539 46.5152C17.5201 46.6418 16.238 47.4622 15.9955 48.0137C15.9556 48.1043 15.9255 48.1708 15.8716 48.2626L15.7691 48.1275C15.4821 47.8862 15.5688 47.6604 15.5848 47.2976C15.7977 47.1256 15.9105 46.8832 16.0651 46.6602C17.6802 44.3303 19.8615 42.4064 21.7238 40.2728C22.3698 39.5327 23.0237 38.904 23.8439 38.3534Z"
        fill="#6DAAFD"
      />
      <path
        d="M15.7691 48.112L15.8715 48.2471C15.9254 48.1553 15.9555 48.0888 15.9954 47.9982C16.238 47.4468 17.5201 46.6263 18.0538 46.4997L18.1222 46.4831C19.4097 46.1782 20.4955 45.9531 21.7853 45.9826C22.1681 45.9915 23.1693 46.2298 23.6366 46.3021C23.6376 47.4719 23.7593 48.7271 23.8488 49.8999L23.8615 57.4925C23.8609 59.8246 23.8017 62.8168 25.549 64.647C26.1666 65.2938 27.0572 65.4996 27.4525 65.8784C27.0285 65.8782 26.6046 65.8679 26.1811 65.8472C22.6724 65.6495 19.7853 64.8092 17.3575 62.1774C14.952 59.5695 15.448 56.6042 15.4056 53.3336C15.379 51.2871 15.3671 49.2084 15.4921 47.172L15.5848 47.2822C15.5688 47.6449 15.4821 47.8707 15.7691 48.112Z"
        fill="#66A1F0"
      />
      <path
        d="M15.5723 28.1819L15.6739 28.2672C15.8466 28.2503 16.0873 27.8777 16.3131 27.6409C17.7948 26.0875 21.7375 26.2215 23.8505 26.3196L23.8431 38.3513C23.0228 38.9019 22.369 39.5306 21.723 40.2707C19.8606 42.4043 17.6793 44.3282 16.0643 46.6581C15.9096 46.8811 15.7969 47.1235 15.584 47.2955L15.4913 47.1854C15.4169 46.4286 15.45 44.9298 15.4479 44.14L15.4361 38.5234L15.4272 32.3393C15.4268 31.4172 15.3635 28.9609 15.5723 28.1819Z"
        fill="#4F86C9"
      />
    </svg>
  );
}

/** CG Sign - a signature stroke. */
export function SignIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path
        d="M54.3096 50.6709L54.6212 50.2281C54.7118 50.0974 54.5328 49.566 54.3936 49.6147C54.0422 49.7365 53.9317 50.1638 53.7792 50.3521C49.9713 55.0576 44.3335 60.0642 39.8338 55.8636C34.2711 61.0851 27.2166 67.6462 19.7223 68.2308C15.2447 68.5807 11.6247 65.8349 11.1142 61.3154C9.9141 50.6886 18.4117 35.3564 25.5038 26.6827C29.3935 21.9241 35.8292 15.7438 40.6891 18.8284C46.1369 22.285 43.4649 31.6363 39.9089 37.5398C39.2768 38.5894 37.7961 38.4321 37.0602 38.1022C34.0766 36.7647 38.6448 32.6039 39.184 26.0937C39.2481 25.3231 39.184 23.2062 38.1453 23.2217C35.9242 23.2549 34.1009 25.146 32.576 26.6517C27.5547 31.6141 23.9744 37.4224 20.9025 43.7732C18.3012 49.1519 14.1883 59.5261 17.1012 62.5953C18.9753 64.5705 24.6331 61.8424 28.2332 59.4863C32.3925 56.7648 35.9087 53.4057 39.4647 49.9601C40.1255 47.1147 41.5753 44.9003 43.2594 42.6439C45.339 39.6456 48.1701 37.1921 51.6045 36.0583C53.9803 35.2745 55.9473 36.3595 57.2402 38.1487C58.3761 38.1133 60.5707 37.6483 60.9774 39.1695C61.5719 41.3905 60.5353 43.6824 60.7586 46.3352C65.8859 41.9463 87.0096 28.1464 87.94 30.8125C88.0527 31.1336 88.0549 31.7868 87.5112 32.15C77.4246 38.906 67.928 46.2046 58.7209 54.101C56.9109 60.3078 54.9505 66.0342 51.6156 71.4173C48.2806 76.8004 42.5455 83.3283 38.0481 80.1751C33.8711 77.2477 37.858 70.2127 41.3874 65.5603C44.9014 60.9278 48.8685 56.9398 53.1891 53.058C53.8809 52.4358 54.2013 51.5832 54.3096 50.6709ZM55.3749 40.7262C54.2544 40.0243 52.8908 40.0841 51.9736 40.7927C48.2452 43.6735 45.0097 47.4867 43.8251 52.438C48.5768 51.333 52.7073 45.2037 55.3749 40.7262ZM40.9057 76.2557C44.4108 74.2517 46.1567 71.1914 47.9822 68.2109C49.1757 65.8393 50.4244 63.7534 51.0476 60.981L47.5203 64.659C44.4351 67.8765 41.6615 71.6276 40.9057 76.2557Z"
        fill="#38C5B6"
      />
    </svg>
  );
}

/** Meetings - a calendar block. */
export function MeetingsIcon(props: IconProps) {
  const id = useId();
  return (
    <svg {...svgProps(props)}>
      <rect
        x="24.0536" y="27.1058" width="50.8929" height="50.8929" rx="9.16071"
        stroke="#A187F5" strokeWidth="6.10714"
      />
      <g filter={`url(#${id}-a)`}>
        <rect x="57.6427" y="17.9462" width="6.10714" height="18.3214" rx="3.05357" fill="#A187F5" />
      </g>
      <g filter={`url(#${id}-b)`}>
        <rect x="35.25" y="17.9462" width="6.10714" height="18.3214" rx="3.05357" fill="#A187F5" />
      </g>
      <defs>
        <filter id={`${id}-a`} x="55.607" y="15.9105" width="10.1786" height="22.3929"
                filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="1.01786" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="shadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="shadow" result="shape" />
        </filter>
        <filter id={`${id}-b`} x="33.2143" y="15.9105" width="10.1786" height="22.3929"
                filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="1.01786" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="shadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="shadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

/** CG Vault - a padlock. */
export function VaultIcon(props: IconProps) {
  const id = useId();
  return (
    <svg {...svgProps(props)}>
      <rect
        x="29.6074" y="45.6423" width="40.7142" height="32.1428" rx="5.35713"
        stroke="#ECAE41" strokeWidth="6.42856" strokeLinejoin="round"
      />
      <g filter={`url(#${id}-a)`}>
        <path d="M39.2492 44.5701V45.6415" stroke="black" strokeOpacity="0.1"
              strokeWidth="6.42856" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <path
        d="M37.1071 45.6428V32.7857C37.1071 26.2766 42.3838 21 48.8928 21H49.4984C55.673 21 60.6785 26.0055 60.6785 32.1801"
        stroke="#ECAE41" strokeWidth="6.42856" strokeLinecap="round" strokeLinejoin="round"
      />
      <defs>
        <filter id={`${id}-a`} x="34.9635" y="40.2843" width="8.57138" height="9.64284"
                filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="0.535713" result="blur" />
        </filter>
      </defs>
    </svg>
  );
}

/** Creator - scaffolding: the pieces an organisation is built from. */
export function CreatorIcon(props: IconProps) {
  const id = useId();
  return (
    <svg {...svgProps(props)}>
      <rect x="19.5172" y="44.072" width="8.6894" height="28.2406" rx="2.462" fill="#EE70AE" />
      <rect x="71.6514" y="44.072" width="8.6894" height="28.2406" rx="2.462" fill="#EE70AE" />
      <rect x="75.9973" y="44.072" width="8.6894" height="52.1364" rx="2.462"
            transform="rotate(90 75.9973 44.072)" fill="#EE70AE" />
      <g filter={`url(#${id}-a)`}>
        <rect x="12.9999" y="67.9655" width="21.7235" height="13.0341" rx="2.462" fill="#EE70AE" />
      </g>
      <g filter={`url(#${id}-b)`}>
        <rect x="65.1369" y="67.9655" width="21.7235" height="13.0341" rx="2.462" fill="#EE70AE" />
      </g>
      <g filter={`url(#${id}-c)`}>
        <rect x="45.0047" y="42.6206" width="9.84799" height="9.84799" rx="2.462" fill="#EE70AE" />
      </g>
      <rect x="45.0047" y="18" width="9.84799" height="34.7142" rx="2.462" fill="#EE70AE" />
      <g filter={`url(#${id}-d)`}>
        <rect x="32.6958" y="18" width="34.468" height="12.31" rx="2.462" fill="#EE70AE" />
      </g>
      <defs>
        {[
          { k: 'a', x: '10.5379', y: '65.5035', w: '26.6475', h: '17.9581', dx: undefined, o: '0.25' },
          { k: 'b', x: '62.6749', y: '65.5035', w: '26.6475', h: '17.9581', dx: undefined, o: '0.25' },
          { k: 'c', x: '45.0047', y: '40.1586', w: '14.772', h: '14.772', dx: '2.462', o: '0.2' },
          { k: 'd', x: '30.2338', y: '15.538', w: '39.392', h: '17.234', dx: undefined, o: '0.25' },
        ].map((f) => (
          <filter key={f.k} id={`${id}-${f.k}`} x={f.x} y={f.y} width={f.w} height={f.h}
                  filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix in="SourceAlpha" type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
            <feOffset {...(f.dx ? { dx: f.dx } : {})} />
            <feGaussianBlur stdDeviation="1.231" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix"
              values={`0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 ${f.o} 0`} />
            <feBlend mode="normal" in2="BackgroundImageFix" result="shadow" />
            <feBlend mode="normal" in="SourceGraphic" in2="shadow" result="shape" />
          </filter>
        ))}
      </defs>
    </svg>
  );
}

/** Audit Trail - a history arrow. */
export function AuditIcon(props: IconProps) {
  const id = useId();
  return (
    <svg {...svgProps(props)}>
      <path
        d="M68.8824 35.5114C76.8586 45.5598 75.5942 60.1298 65.4643 68.6437C58.1485 74.7934 48.2574 75.9023 39.8113 71.6229C32.1381 67.7359 26.8576 59.6475 26.8362 50.5108C26.8323 48.4258 25.2534 47.0006 23.3152 47.0143C21.6236 47.026 20.0583 48.4551 20.0428 50.4444C19.9496 62.3124 27.0382 72.9797 37.0865 77.8839C48.2866 83.3503 61.2558 81.4722 70.3642 73.4483C82.6634 62.6092 84.2831 44.2225 74.394 31.5307C64.4544 18.7764 46.3833 16.0783 32.9907 25.3693C32.9907 25.3693 30.3736 28.8104 31.3517 30.4409C32.3298 32.0713 36.0417 31.7474 36.0417 31.7474C46.3891 23.5107 60.9995 25.5782 68.8824 35.5114Z"
        fill="#E34546"
      />
      <g filter={`url(#${id}-a)`}>
        <path
          d="M41.7311 32.1617C39.8978 32.0348 38.0547 32.4779 36.0408 31.7458L33.6138 29.3098C32.9898 25.4677 32.9898 25.3677 32.9898 25.3677C32.5042 21.9082 33.718 18.7455 30.2631 18.0525C28.9541 17.7889 26.8625 18.5152 26.5867 20.3015L24.3514 34.809C23.9824 37.2064 26.0002 38.6218 28.1812 38.6784L41.3738 39.0239C43.5392 39.0806 45.1473 37.8077 45.2444 35.7949C45.3415 33.7821 43.9062 32.3139 41.7311 32.1617Z"
          fill="#E34546"
        />
      </g>
      <defs>
        <filter id={`${id}-a`} x="19.7836" y="13.4761" width="29.9894" height="30.0736"
                filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix in="SourceAlpha" type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset />
          <feGaussianBlur stdDeviation="2.26196" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend mode="normal" in2="BackgroundImageFix" result="shadow" />
          <feBlend mode="normal" in="SourceGraphic" in2="shadow" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}

/**
 * Module id -> brand mark. Keyed by ModuleId rather than by an icon name,
 * so adding a seventh module is a compile error here until it has a mark,
 * rather than a blank square at runtime.
 */
export const MODULE_BRAND_ICON: Record<ModuleId, (props: IconProps) => ReactElement> = {
  todo: TodoIcon,
  sign: SignIcon,
  meetings: MeetingsIcon,
  vault: VaultIcon,
  creator: CreatorIcon,
  audit: AuditIcon,
};

/** The accent each mark is drawn in, for tinting the plate behind it. */
export const MODULE_BRAND_COLOR: Record<ModuleId, string> = {
  todo: '#6DAAFD',
  sign: '#38C5B6',
  meetings: '#A187F5',
  vault: '#ECAE41',
  creator: '#EE70AE',
  audit: '#E34546',
};
